import React, { useEffect, useState } from 'react';

import { NotificationCard } from '../../components/notifications/notificationCard';
import { SystemCard } from '../../components/notifications/systemCard';
import config from '../../config';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useNotifications } from '../../context/NotificationContext';
import DotSpinner from '../../components/shared/DotSpinner';
import { useSubscription } from '../../hooks/useSubscription';
import { NoSubscribeLikeList } from './NoSubscribeLikeList';
import { useLocation } from 'react-router-dom';

const Notifications = () => {
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const { isActiveSubscription } = useSubscription();

  const { counts, updateCounts, setLoading: setGlobalLoading } = useNotifications();

  const [option, setOption] = useState(location.state?.from || 1); // 1 - симпатии, 2 - лайки, 3 - системные

  const [notifications, setNotifications] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const markAsRead = async (notificationId) => {
    try {
      await axiosPrivate.post(config.API.NOTIFICATIONS.MARK_READ(notificationId));

      // Обновляем локальное состояние
      setNotifications((prev) => prev.map((n) => (n._id === notificationId ? { ...n, seen: true } : n)));
      setSystemNotifications((prev) => prev.map((n) => (n._id === notificationId ? { ...n, seen: true } : n)));

      // Обновляем счётчики
      try {
        const response = await axiosPrivate.get(config.API.NOTIFICATIONS.COUNTS);
        if (response.data?.status === 'success') {
          updateCounts(response.data.data);
        }
      } catch (err) {
        console.error('Error refreshing counts:', err);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  useEffect(() => {
    const loadCounts = async () => {
      try {
        setGlobalLoading(true);
        const response = await axiosPrivate.get(config.API.NOTIFICATIONS.COUNTS);

        if (response.data?.status === 'success') {
          updateCounts(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching notification counts:', error);
      } finally {
        setGlobalLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadCounts();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadNotifications = async (type) => {
      setLoading(true);
      try {
        const response = await axiosPrivate.get(config.API.NOTIFICATIONS.LIST, {
          params: { type, limit: 50 }
        });

        if (response.data?.status === 'success') {
          const items = response.data.data.items || [];

          if (type === 'system') {
            // сразу показываем как прочитанные в UI
            const seenItems = items.map((n) => ({ ...n, seen: true }));
            setSystemNotifications(seenItems);

            // авто-прочтение всех непрочитанных на бэке
            const unread = items.filter((n) => !n.seen);
            if (unread.length > 0) {
              await Promise.all(
                unread.map((n) =>
                  axiosPrivate
                    .post(config.API.NOTIFICATIONS.MARK_READ(n._id))
                    .catch((err) => console.error('mark read error', err))
                )
              );

              // обновляем счётчики после массового прочтения
              try {
                const countsResp = await axiosPrivate.get(config.API.NOTIFICATIONS.COUNTS);
                if (countsResp.data?.status === 'success') {
                  updateCounts(countsResp.data.data);
                }
              } catch (err) {
                console.error('Error refreshing counts:', err);
              }
            }
          } else {
            setNotifications(items);
          }
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    if (option === 1) {
      loadNotifications('mutual,superlike');
    } else if (option === 2) {
      loadNotifications('like');
    } else if (option === 3) {
      loadNotifications('system');
    }
  }, [option]);

  return (
    <div className="w-full pb-[80px] flex flex-col items-center overflow-hidden">
      <div className="relative overflow-scroll">
        <div className="fixed z-10 left-1/2 -translate-x-1/2 flex items-center pl-[16px] pr-[16px] w-[100vw]">
          <div className="rounded-[400px] flex flex-row bg-[#FFFFFF1A] h-[44px] pl-[10px] pr-[10px] w-full justify-center items-center text-white">
            <div
              className={`w-[167px] h-[33px] pt-1 pb-1 font-raleway 
                  flex justify-center items-center cursor-pointer rounded-[400px] text-center
                  ${option === 1 ? 'bg-[#FFFFFF1A] text-[#A1F69E]' : 'bg-transparent'} `}
              onClick={() => setOption(1)}
            >
              Симпатии ({counts.mutuals || 0})
            </div>
            <div
              className={`w-[167px] h-[33px] pt-1 pb-1 font-raleway 
                  flex justify-center items-center rounded-[400px] cursor-pointer text-center
                  ${option === 2 ? 'bg-[#FFFFFF1A] text-[#A1F69E]' : 'bg-transparent'}`}
              onClick={() => setOption(2)}
            >
              Лайки ({counts.likes || 0})
            </div>
            <div
              className={`w-[167px] h-[33px] pt-1 pb-1 font-raleway 
                  flex justify-center items-center rounded-[400px] cursor-pointer text-center
                  ${option === 3 ? 'bg-[#FFFFFF1A] text-[#A1F69E]' : 'bg-transparent'}`}
              onClick={() => setOption(3)}
            >
              Системные ({counts.system || 0})
            </div>
          </div>
        </div>

        <div className="mt-[70px]">
          {loading ? (
            <DotSpinner />
          ) : option === 1 ? (
            notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  className="border-b border-[#233636] w-[100vw]"
                  onClick={() => !notification.seen && markAsRead(notification._id)}
                >
                  <NotificationCard notification={notification} onRead={markAsRead} option={option} />
                </div>
              ))
            ) : (
              <div className="flex flex-col justify-center items-center h-[400px] text-white text-center px-4 gap-7">
                <img src="/icons/bell.svg" className="size-[48px]" />
                <div>
                  <h4 className="text-[24px] font-raleway font-medium">Нет уведомлений</h4>
                  <p>Здесь появятся уведомления</p>
                </div>
              </div>
            )
          ) : option === 2 ? (
            isActiveSubscription ? (
              notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className="border-b border-[#233636] w-[100vw]"
                    onClick={() => !notification.seen && markAsRead(notification._id)}
                  >
                    <NotificationCard notification={notification} onRead={markAsRead} option={option} />
                  </div>
                ))
              ) : (
                <div className="flex flex-col justify-center items-center h-[400px] text-white text-center px-4 gap-7">
                  <img src="/icons/bell.svg" className="size-[48px]" />
                  <div>
                    <h4 className="text-[24px] font-raleway font-medium">Нет уведомлений</h4>
                    <p>Здесь появятся уведомления</p>
                  </div>
                </div>
              )
            ) : (
              <NoSubscribeLikeList notifications={notifications} />
            )
          ) : option === 3 ? (
            systemNotifications.length > 0 ? (
              systemNotifications.map((notification) => (
                <div
                  key={notification._id}
                  className="border-b border-[#233636] w-[100vw]"
                  onClick={() => !notification.seen && markAsRead(notification._id)}
                >
                  <SystemCard notification={notification} onRead={markAsRead} option={option} />
                </div>
              ))
            ) : (
              <div className="flex flex-col justify-center items-center h-[400px] text-white text-center px-4 gap-7">
                <img src="/icons/bell.svg" className="size-[48px]" />
                <div>
                  <h4 className="text-[24px] font-raleway font-medium">Нет уведомлений</h4>
                  <p>Здесь появятся уведомления</p>
                </div>
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
